import type React from "react";
import { useEffect, useState } from "react";

import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { DefaultTheme } from "styled-components";
import styled from "styled-components";

import { DropdownIcon } from "assets";

import { SectionHelp } from ".";

interface Props {
  readonly collapsible?: boolean;
  readonly children: React.ReactNode;
  readonly helpContent?: string | React.ReactNode;
  readonly dataTestId?: string;
  readonly disabled?: boolean;
  readonly collapsedInitially?: boolean;
  readonly backgroundColor?: "blue" | "white" | "default" | "db1";
  readonly label?: string;
  readonly optional?: boolean;
  readonly readOnly?: boolean;
}

const Section: React.FC<Props> = ({
  backgroundColor = "default",
  collapsible = true,
  children,
  dataTestId,
  disabled = false,
  collapsedInitially = false,
  helpContent,
  label,
  optional,
  readOnly,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(!collapsedInitially);
  const toggle = () => {
    if (!disabled && collapsible) setExpanded(!expanded);
  };

  useEffect(() => {
    if (disabled || !collapsible) setExpanded(true);
  }, [disabled]);
  return (
    <StyledAccordion
      expanded={expanded}
      onChange={toggle}
      data-testid={dataTestId}
      $backgroundColor={backgroundColor}
      $readOnly={readOnly}
    >
      {label && (
        <StyledAccordionSummary $collapsible={collapsible} disabled={disabled} expandIcon={<DropdownIcon />}>
          <Typography variant="h5" marginRight={1}>
            {label}
            {optional && <OptionalText> ({t("patients.notes.optional_section")})</OptionalText>}
          </Typography>
          {collapsible && helpContent && label && <SectionHelp>{helpContent}</SectionHelp>}
        </StyledAccordionSummary>
      )}
      <AccordionDetails sx={{ px: 0 }}>
        <Container>{children}</Container>
      </AccordionDetails>
    </StyledAccordion>
  );
};

const getBackgroundColor = (color: string, theme: DefaultTheme) => {
  switch (color) {
    case "blue":
      return theme.colors.pastels.blue;
    case "white":
      return theme.colors.greys.light7;
    case "db1":
      return theme.colors.redesign.db1;
    case "default":
    default:
      return "transparent";
  }
};

const StyledAccordion = styled(Accordion)<{ $backgroundColor: string; $readOnly?: boolean }>`
  &.MuiAccordion-root {
    background-color: ${({ $backgroundColor, theme }) => getBackgroundColor($backgroundColor, theme)};
    box-shadow: none;
    margin: 0 16px;
    border-bottom: 1px solid ${props => (props.$readOnly ? "transparent" : props.theme.colors.redesign.db30)};
    &:before {
      content: none;
    }

    &.Mui-expanded {
      margin: 0 16px;
    }
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)<{ disabled: boolean; $collapsible: boolean }>`
  &.MuiAccordionSummary-root {
    padding-left: 0;
    padding-right: 0;
    &.MuiButtonBase-root,
    &.Mui-expanded:hover,
    .MuiButtonBase-root {
      cursor: ${({ disabled }) => disabled && "not-allowed"};
      cursor: ${({ $collapsible }) => !$collapsible && "default"};
    }
    &.Mui-disabled {
      opacity: 1;
      pointer-events: inherit;
      span {
        opacity: 0.38;
      }
    }
    &.Mui-expanded {
      min-height: 60px;
    }

    .MuiAccordionSummary-expandIcon {
      display: ${({ $collapsible }) => !$collapsible && "none"};
    }
  }
  .MuiAccordionSummary-content {
    align-items: center;
    min-height: 28px;
    margin: 24px 0;
    &.Mui-expanded {
      margin: 24px 0;
    }
  }
`;

const Container = styled.div`
  width: 100%;
`;

const OptionalText = styled.span`
  text-transform: none;
  letter-spacing: 0.5px;
  font-weight: 300;
  color: ${props => props.theme.colors.redesign.db90};
`;

export { Section };
